import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiDownload, FiRefreshCw } from 'react-icons/fi';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

const INITIAL_FILTERS = {
  brands: [],
  stockAvailable: false,
  minPrice: '',
  maxPrice: '',
  published: '',
  uploadedFrom: '',
  uploadedTo: '',
};

function getErrorMessage(response, fallback) {
  return response.json()
    .then((body) => body.error || fallback)
    .catch(() => fallback);
}

export function AdminProductExport({ onUnauthorized }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [options, setOptions] = useState({
    brands: [],
    priceRange: {},
    uploadDateRange: {},
    dateFilterAvailable: true,
  });
  const [brandSearch, setBrandSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadOptions() {
      setLoadingOptions(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE}/api/products/export/options`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
          signal: controller.signal,
        });
        if (response.status === 401 || response.status === 403) {
          onUnauthorized();
          return;
        }
        if (!response.ok) throw new Error(await getErrorMessage(response, 'Export filters could not be loaded.'));
        const data = await response.json();
        setOptions({
          brands: Array.isArray(data.brands) ? data.brands : [],
          priceRange: data.priceRange || {},
          uploadDateRange: data.uploadDateRange || {},
          dateFilterAvailable: data.dateFilterAvailable !== false,
        });
      } catch (loadError) {
        if (loadError.name !== 'AbortError') setError(loadError.message || 'Export filters could not be loaded.');
      } finally {
        if (!controller.signal.aborted) setLoadingOptions(false);
      }
    }

    loadOptions();
    return () => controller.abort();
  }, [onUnauthorized]);

  const visibleBrands = useMemo(() => {
    const query = brandSearch.trim().toLocaleLowerCase();
    return query
      ? options.brands.filter((brand) => brand.toLocaleLowerCase().includes(query))
      : options.brands;
  }, [brandSearch, options.brands]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setError('');
    setSuccess('');
  };

  const toggleBrand = (brand) => {
    setFilters((current) => ({
      ...current,
      brands: current.brands.includes(brand)
        ? current.brands.filter((item) => item !== brand)
        : [...current.brands, brand],
    }));
    setError('');
    setSuccess('');
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setBrandSearch('');
    setError('');
    setSuccess('');
  };

  const handleExport = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (filters.minPrice !== '' && filters.maxPrice !== '' && Number(filters.minPrice) > Number(filters.maxPrice)) {
      setError('Minimum price cannot be greater than maximum price.');
      return;
    }
    if (filters.uploadedFrom && filters.uploadedTo && filters.uploadedFrom > filters.uploadedTo) {
      setError('Upload date from cannot be after upload date to.');
      return;
    }

    const params = new URLSearchParams();
    filters.brands.forEach((brand) => params.append('brand', brand));
    if (filters.stockAvailable) params.set('stockAvailable', 'true');
    if (filters.minPrice !== '') params.set('minPrice', filters.minPrice);
    if (filters.maxPrice !== '') params.set('maxPrice', filters.maxPrice);
    if (filters.published) params.set('published', filters.published);
    if (filters.uploadedFrom) params.set('uploadedFrom', filters.uploadedFrom);
    if (filters.uploadedTo) params.set('uploadedTo', filters.uploadedTo);

    setExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/products/export.csv?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (response.status === 401 || response.status === 403) {
        onUnauthorized();
        return;
      }
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Products could not be exported.'));

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      setSuccess('CSV exported successfully.');
    } catch (exportError) {
      setError(exportError.message || 'Products could not be exported.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="va-card va-export-card" role="tabpanel" id="products-export-panel" aria-labelledby="products-export-tab">
      <div className="va-card__header">
        <div>
          <h2>Export product CSV</h2>
          <p>Choose any combination of filters. Leaving everything blank exports the complete catalog.</p>
        </div>
        <span className="va-results">{filters.brands.length} brand{filters.brands.length === 1 ? '' : 's'} selected</span>
      </div>

      <form className="va-card__body" onSubmit={handleExport}>
        {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}
        {success && <div className="va-alert va-alert--success" role="status"><FiCheck /> {success}</div>}

        <div className="va-export-grid">
          <div className="va-field va-export-brands-field">
            <div className="va-export-label-row">
              <label htmlFor="export-brand-search">Brands <span>(select one or more)</span></label>
              {filters.brands.length > 0 && <button type="button" className="va-text-button" onClick={() => updateFilter('brands', [])}>Clear selection</button>}
            </div>
            <input id="export-brand-search" className="va-input" type="search" placeholder="Find a brand" value={brandSearch} onChange={(event) => setBrandSearch(event.target.value)} />
            <div className="va-brand-options" aria-label="Available brands">
              {loadingOptions ? (
                <div className="va-brand-options__state"><div className="va-spinner" /> Loading brands...</div>
              ) : visibleBrands.length ? visibleBrands.map((brand) => (
                <label className="va-brand-option" key={brand}>
                  <input type="checkbox" checked={filters.brands.includes(brand)} onChange={() => toggleBrand(brand)} />
                  <span>{brand}</span>
                </label>
              )) : (
                <div className="va-brand-options__state">No brands found.</div>
              )}
            </div>
            <small>No brand selected means all brands.</small>
          </div>

          <div className="va-export-filter-stack">
            <div className="va-field">
              <label htmlFor="export-published">Published status</label>
              <select id="export-published" className="va-input" value={filters.published} onChange={(event) => updateFilter('published', event.target.value)}>
                <option value="">All products</option>
                <option value="Yes">Published</option>
                <option value="No">Not published</option>
              </select>
            </div>

            <label className="va-check va-export-check">
              <input type="checkbox" checked={filters.stockAvailable} onChange={(event) => updateFilter('stockAvailable', event.target.checked)} />
              <span><strong>In stock only</strong><span>Export products whose product_stock is greater than zero.</span></span>
            </label>

            <fieldset className="va-export-fieldset">
              <legend>Price range</legend>
              <div className="va-export-pair">
                <div className="va-field">
                  <label htmlFor="export-min-price">Minimum</label>
                  <input id="export-min-price" className="va-input" type="number" min="0" max="100000000" step="0.01" placeholder={options.priceRange.min ?? '0.00'} value={filters.minPrice} onChange={(event) => updateFilter('minPrice', event.target.value)} />
                </div>
                <div className="va-field">
                  <label htmlFor="export-max-price">Maximum</label>
                  <input id="export-max-price" className="va-input" type="number" min="0" max="100000000" step="0.01" placeholder={options.priceRange.max ?? 'No limit'} value={filters.maxPrice} onChange={(event) => updateFilter('maxPrice', event.target.value)} />
                </div>
              </div>
            </fieldset>

            <fieldset className="va-export-fieldset" disabled={!options.dateFilterAvailable}>
              <legend>Upload date</legend>
              <div className="va-export-pair">
                <div className="va-field">
                  <label htmlFor="export-uploaded-from">From</label>
                  <input id="export-uploaded-from" className="va-input" type="date" min={options.uploadDateRange.min || undefined} max={filters.uploadedTo || options.uploadDateRange.max || undefined} value={filters.uploadedFrom} onChange={(event) => updateFilter('uploadedFrom', event.target.value)} />
                </div>
                <div className="va-field">
                  <label htmlFor="export-uploaded-to">To</label>
                  <input id="export-uploaded-to" className="va-input" type="date" min={filters.uploadedFrom || options.uploadDateRange.min || undefined} max={options.uploadDateRange.max || undefined} value={filters.uploadedTo} onChange={(event) => updateFilter('uploadedTo', event.target.value)} />
                </div>
              </div>
              <small>{options.dateFilterAvailable ? 'The end date includes the entire selected day.' : 'Upload date becomes available after the database migration.'}</small>
            </fieldset>
          </div>
        </div>

        <div className="va-form-actions">
          <button className="va-button va-button--secondary" type="button" onClick={resetFilters} disabled={exporting}><FiRefreshCw /> Reset filters</button>
          <button className="va-button" type="submit" disabled={exporting || loadingOptions}><FiDownload /> {exporting ? 'Exporting...' : 'Export CSV'}</button>
        </div>
      </form>
    </section>
  );
}
